import {EmptyPolicyNumber, PolicyNumber, ValidPolicyNumber} from 'ApplicationEntry/models/EnteredPolicyNumber';
import {EmptyString, isNonEmptyString, NonEmptyString, StringUnion} from 'Core/models/EmptyString';
import {ToPartialMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';
import {isLoaded as isLoadedPackages, PackageModel, UnassignedPackageModel} from '../PackageModel';

type PinCareProps = {
  packageName: StringUnion;
  planType: StringUnion;
  premium: StringUnion;
  calculatedPremium: StringUnion;
  cwa: StringUnion;
  replacement: boolean;
  policyNumber: PolicyNumber;
  packageList: PackageModel;
  units: StringUnion;
  jStamp: boolean;
  situsState: StringUnion;
};

export class InvalidNewYorkPinCare {
  public readonly key = 'InvalidNewYorkPinCare';

  constructor(
    public readonly packageName: StringUnion,
    public readonly planType: StringUnion,
    public readonly premium: StringUnion,
    public readonly calculatedPremium: StringUnion,
    public readonly cwa: StringUnion,
    public readonly replacement: boolean,
    public readonly policyNumber: PolicyNumber,
    public readonly packageList: PackageModel,
    public readonly units: StringUnion,
    public readonly jStamp: boolean,
    public readonly situsState: StringUnion
  ) {}
}

export class ValidNewYorkPinCare {
  public static createApp({
    packageName,
    planType,
    premium,
    calculatedPremium,
    cwa,
    replacement,
    policyNumber,
    packageList,
    units,
    jStamp,
    situsState,
  }: PinCareProps) {
    return this.create(
      packageName,
      planType,
      premium,
      calculatedPremium,
      cwa,
      replacement,
      policyNumber,
      packageList,
      units,
      jStamp,
      situsState
    );
  }

  public static create(
    packageName: StringUnion,
    planType: StringUnion,
    premium: StringUnion,
    calculatedPremium: StringUnion,
    cwa: StringUnion,
    replacement: boolean,
    policyNumber: PolicyNumber,
    packageList: PackageModel,
    units: StringUnion,
    jStamp: boolean,
    situsState: StringUnion
  ) {
    return isNonEmptyString(packageName) &&
      planType &&
      isNonEmptyString(premium) &&
      isNonEmptyString(cwa) &&
      replacement &&
      policyNumber &&
      isLoadedPackages(packageList)
      ? new ValidNewYorkPinCare(
          packageName,
          planType,
          premium,
          calculatedPremium,
          cwa,
          replacement,
          policyNumber,
          packageList,
          units,
          jStamp,
          situsState
        )
      : new InvalidNewYorkPinCare(
          packageName,
          planType,
          premium,
          calculatedPremium,
          cwa,
          replacement,
          policyNumber,
          packageList,
          units,
          jStamp,
          situsState
        );
  }

  public readonly key = 'ValidNewYorkPinCare';
  constructor(
    public readonly packageName: StringUnion,
    public readonly planType: StringUnion,
    public readonly premium: StringUnion,
    public readonly calculatedPremium: StringUnion,
    public readonly cwa: StringUnion,
    public readonly replacement: boolean,
    public readonly policyNumber: PolicyNumber,
    public readonly packageList: PackageModel,
    public readonly units: StringUnion,
    public readonly jStamp: boolean,
    public readonly situsState: StringUnion
  ) {}
}

export type NewYorkPinCare = InvalidNewYorkPinCare | ValidNewYorkPinCare;

export type NewYorkPinCareOptions<TReturn> = ToPartialMatchOptions<NewYorkPinCare, 'key', TReturn>;

export const matchNewYorkPinCare = <TReturn>(options: NewYorkPinCareOptions<TReturn>) => (
  app: NewYorkPinCare
): TReturn => {
  switch (app.key) {
    case 'InvalidNewYorkPinCare':
      return options.InvalidNewYorkPinCare ? options.InvalidNewYorkPinCare(app) : options.default(app);
    case 'ValidNewYorkPinCare':
      return options.ValidNewYorkPinCare ? options.ValidNewYorkPinCare(app) : options.default(app);
  }
};

export const EmptyNewYorkPinCare = new InvalidNewYorkPinCare(
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  false,
  EmptyPolicyNumber,
  new UnassignedPackageModel(),
  new EmptyString(),
  false,
  new EmptyString()
);

export const changePackage = (packageName: string) => (app: NewYorkPinCare) =>
  ValidNewYorkPinCare.createApp({...app, packageName: NonEmptyString.create(packageName)});

export const changePlanType = (planType: string) => (app: NewYorkPinCare) =>
  ValidNewYorkPinCare.createApp({...app, planType: NonEmptyString.create(planType)});

export const changePremium = (premium: string) => (app: NewYorkPinCare) =>
  ValidNewYorkPinCare.createApp({...app, premium: NonEmptyString.create(premium)});

export const changeCalculatedPremium = (calculatedPremium: string) => (app: NewYorkPinCare) =>
  ValidNewYorkPinCare.createApp({...app, calculatedPremium: NonEmptyString.create(calculatedPremium)});

export const changeCWA = (cwa: string) => (app: NewYorkPinCare) =>
  ValidNewYorkPinCare.createApp({...app, cwa: NonEmptyString.create(cwa)});

export const changeReplacement = (replacement: boolean) => (app: NewYorkPinCare) =>
  ValidNewYorkPinCare.createApp({...app, replacement, policyNumber: ValidPolicyNumber.create(new EmptyString())});

export const changePolicyNumber = (policyNumber: StringUnion) => (app: NewYorkPinCare) =>
  ValidNewYorkPinCare.createApp({...app, policyNumber: ValidPolicyNumber.create(policyNumber)});

export const changePackageList = (packages: PackageModel) => (app: NewYorkPinCare) =>
  ValidNewYorkPinCare.createApp({...app, packageList: packages});

export const changeUnits = (units: string) => (app: NewYorkPinCare) =>
  ValidNewYorkPinCare.createApp({...app, units: NonEmptyString.create(units)});

export const changeJStamp = (jStamp: boolean) => (app: NewYorkPinCare) =>
  ValidNewYorkPinCare.createApp({...app, jStamp});

export const changeSitusState = (situsState: string) => (app: NewYorkPinCare) =>
  ValidNewYorkPinCare.createApp({...app, situsState: NonEmptyString.create(situsState)});
